<template>
    <div class="navbar-collapse collapse">
        <ul class="nav navbar-nav hidden-sm hidden-xs hidden-md legacy:tw-m-0">
            <!-- User Impersonation -->
            <li v-if="user.impersonated">
                <a
                    v-b-popover.hover.bottom="$t(`menu.endImpersonation`)"
                    href="#/auth/login"
                    tabindex="0"
                    class="tw-inline-block legacy:tw-bg-indigo-900"
                    @click="onLogout"
                >
                    <i class="fal fa-user-lock" />
                    <span class="tw-sr-only">{{ $t(`menu.endImpersonation`) }}</span>
                </a>
            </li>
            <!--/User Impersonation -->

            <l-nav-menu-element
                v-if="isLoggedIn"
                href="#/profile"
                :text="$t('menu.profile')"
                :options="{ testId: 'menu-profile' }"
                :show="true"
            >
                <l-profile-picture :user="user" size="xs" class="user-picture" />
            </l-nav-menu-element>
            <l-nav-menu-element
                v-for="element in menu.main"
                :key="element.href"
                :show="element.show"
                :href="element.href"
                :text="element.text"
                :options="element.options"
            />
            <li
                v-if="isLoggedIn"
                class="tw-cursor-pointer"
                :class="[
                    'dropdown',
                    { open: isCreateDropdownOpen },
                    { active: isCreateDropdownActive },
                ]"
                tabindex="0"
                style="transform:none !important"
                @click.stop="toggleCreateDropdown()"
                @keyup.enter="toggleCreateDropdown()"
            >
                <a class="dropdown-toggle" data-test-id="menu-create-dropdown" @click.prevent>
                    <i class="fas fa-bolt legacy:tw-m-0 dropdown-icon" />
                    <span class="tw-sr-only">Create Dropdown</span>
                </a>
                <ul class="dropdown-menu" style="right: 0; left: auto">
                    <l-nav-menu-element
                        v-for="element in menu.create"
                        :key="element.href"
                        :show="element.show"
                        :href="element.href"
                        :text="element.text"
                        :options="element.options"
                        :is-in-dropdown="true"
                        @navigation="onCreateDropdownClose()"
                    />
                </ul>
            </li>
            <li
                v-if="isLoggedIn"
                class="tw-cursor-pointer"
                :class="['dropdown', { open: isDropdownOpen }, { active: isDropdownActive }]"
                tabindex="0"
                @click.stop="toggleDropdown()"
                @keyup.enter="toggleDropdown()"
            >
                <a class="dropdown-toggle" data-test-id="menu-dropdown" @click.prevent>
                    <i class="fal fa-cog legacy:tw-m-0 dropdown-icon" />
                    <!-- Content needed for accessibility -->
                    <span style="display:none;">Menu Dropdown</span>
                </a>
                <ul class="dropdown-menu" style="right: 0; left: auto">
                    <l-nav-menu-element
                        v-for="element in menu.settings"
                        :key="element.href"
                        :show="element.show"
                        :href="element.href"
                        :text="element.text"
                        :options="element.options"
                        :is-in-dropdown="true"
                        @navigation="onDropdownClose()"
                    />
                    <li>
                        <a
                            id="logout"
                            href="#/auth/login"
                            data-test-id="menu-logout"
                            @click="onLogout"
                        >
                            {{
                                user.impersonated ? $t(`menu.endImpersonation`) : $t("menu.logout")
                            }}
                        </a>
                    </li>
                </ul>
            </li>
            <l-nav-menu-element
                v-if="!isLoggedIn"
                href="#/auth/login"
                :text="$t('menu.login')"
                :options="{ testId: 'menu-login' }"
                :show="true"
            >
                <i class="fal fa-lock tw-mr-2" />
            </l-nav-menu-element>
        </ul>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LNavMenuElement from "@/components/LNavMenuElement";
import LProfilePicture from "@/components/LProfilePicture";

export default {
    components: {
        LNavMenuElement,
        LProfilePicture,
    },
    props: {
        menu: {
            type: Object,
            required: true,
        },
        logout: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            isDropdownOpen: false,
            isCreateDropdownOpen: false,
        };
    },
    computed: {
        ...mapGetters({
            isLoggedIn: "auth/isLoggedIn",
            user: "users/current/user",
        }),
        isDropdownActive() {
            return (
                this.menu.settings?.some(element =>
                    `#${this.$route.path}`.startsWith(element.href),
                ) ?? false
            );
        },
        isCreateDropdownActive() {
            return (
                this.menu.create?.some(element =>
                    `#${this.$route.path}`.startsWith(element.href),
                ) ?? false
            );
        },
    },
    mounted() {
        document.addEventListener("click", this.onDropdownClose);
        document.addEventListener("click", this.onCreateDropdownClose);
    },
    beforeUnmount() {
        document.removeEventListener("click", this.onDropdownClose);
        document.removeEventListener("click", this.onCreateDropdownClose);
    },
    methods: {
        toggleDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen;
            if (this.isCreateDropdownOpen && this.isDropdownOpen) {
                this.isCreateDropdownOpen = false;
            }
        },
        onDropdownClose() {
            this.isDropdownOpen = false;
        },
        toggleCreateDropdown() {
            this.isCreateDropdownOpen = !this.isCreateDropdownOpen;
            if (this.isDropdownOpen && this.isCreateDropdownOpen) {
                this.isDropdownOpen = false;
            }
        },
        onCreateDropdownClose() {
            this.isCreateDropdownOpen = false;
        },
        onLogout(event) {
            if (event) event.preventDefault();
            this.onDropdownClose();
            this.logout();
        },
    },
};
</script>

<style scoped>
.user-picture {
    display: inline-block;
    margin-right: 8px;
    position: relative;
    top: -2px;
}

.dropdown-icon {
    transform: none !important;
}

/* Remove once using tailwind JIT or legacy styles got removed */
.legacy\:tw-m-0 {
    margin: 0 !important;
}

.legacy\:tw-bg-indigo-900 {
    @apply tw-bg-indigo-900 !important;
}
</style>
